import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "isomorphic-dompurify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Chatbot from "../components/Chatbot";
import { fetchPublicPost } from "../lib/api";

// Minimal markdown-to-HTML (headings, bold, italic, links, paragraphs, lists)
function renderMarkdown(md) {
  if (!md) return "";
  let html = md
    .replace(/^### (.*$)/gim, '<h3 class="font-serif text-2xl text-forest mt-10 mb-3">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="font-serif text-3xl text-forest mt-12 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="font-serif text-4xl text-forest mt-12 mb-6">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-ink">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a class="text-forest underline" href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // lists
  html = html.replace(/(?:^|\n)([-*] .+(?:\n[-*] .+)*)/g, (m, block) => {
    const items = block.split(/\n/).map((l) => l.replace(/^[-*] /, "").trim()).filter(Boolean);
    return "\n<ul class='list-disc pl-6 my-4 space-y-2 text-graphite'>" +
      items.map((i) => `<li>${i}</li>`).join("") + "</ul>";
  });
  // paragraphs (split on blank lines)
  html = html
    .split(/\n{2,}/)
    .map((para) => (para.match(/^<(h\d|ul|li|p|blockquote)/) ? para : `<p class='text-graphite text-base md:text-lg leading-relaxed my-5'>${para.replace(/\n/g, "<br/>")}</p>`))
    .join("\n");
  return html;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchPublicPost(slug)
      .then((p) => {
        setPost(p);
        document.title = `${p.title} · Greenroute`;
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/404" replace />;
  if (loading) {
    return (
      <main className="bg-cream min-h-screen">
        <Navbar />
        <div className="pt-40 text-center text-graphite">Loading…</div>
      </main>
    );
  }
  if (!post) return null;

  return (
    <main className="bg-cream" data-testid="blog-post-page">
      <Navbar />
      <article className="pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sage text-sm tracking-wide link-underline mb-10"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={14} /> All insights
          </Link>
          <div className="uppercase text-xs tracking-[0.3em] text-sage mb-6">
            {new Date(post.created_at).toLocaleDateString("en-ZA", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
          <h1
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight mb-8"
            data-testid="post-title"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="font-serif text-xl md:text-2xl text-ink/80 italic leading-snug mb-10 border-l-2 border-gold pl-6">
              {post.excerpt}
            </p>
          )}
          {post.cover_image && (
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-80 md:h-[460px] object-cover mb-12"
            />
          )}
          <div
            className="prose"
            data-testid="post-body"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(renderMarkdown(post.body), {
                ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "a", "br", "blockquote", "code", "pre"],
                ALLOWED_ATTR: ["href", "target", "rel", "class"],
              }),
            }}
          />

          {/* Ask the article — opens chatbot pre-seeded with this post's content */}
          <div className="mt-16 border-t border-bordersoft pt-10" data-testid="ask-article-cta">
            <div className="bg-forest text-cream p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="max-w-xl">
                <div className="uppercase text-[10px] tracking-[0.3em] text-gold mb-3">
                  Talk it through
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                  Ask Greenroute Assistant about this article.
                </h3>
                <p className="text-cream/75 mt-4 text-sm md:text-base">
                  Curious how this applies to your specific estate? Our AI is briefed
                  on the article and ready to answer your follow-up questions.
                </p>
              </div>
              <button
                onClick={() => {
                  const plain = (post.body || "").replace(/[#*_`>]/g, "").slice(0, 2000);
                  const context = `Article title: "${post.title}"\n${post.excerpt ? `Article excerpt: ${post.excerpt}\n` : ""}Article body:\n${plain}`;
                  const intro = `You've opened a conversation about "${post.title}". Ask me anything — how it applies to your property, what it'll cost, or how Greenroute would handle it.`;
                  const evt = new CustomEvent("gr:chat-seed", {
                    detail: { label: post.title, context, intro },
                  });
                  window.dispatchEvent(evt);
                }}
                data-testid="ask-article-btn"
                className="shrink-0 bg-cream text-forest px-7 py-4 text-sm tracking-wider hover:bg-gold hover:text-cream transition-colors inline-flex items-center gap-2"
              >
                Ask the article
              </button>
            </div>
          </div>
        </div>
      </article>
      <Footer />
      <Chatbot />
    </main>
  );
}
